import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CostAdjustmentDetailModel } from 'src/app/models/inventory/cost-adjustment-detail.model';
import { CostAdjustmentModel } from 'src/app/models/inventory/cost-adjustment.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { CostAdjustmentDetailService } from 'src/app/services/inventory/cost-adjustment-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-cost-adjustment-detail',
  templateUrl: './register-cost-adjustment-detail.component.html',
  styleUrls: ['./register-cost-adjustment-detail.component.scss'],
  standalone: false
})
export class RegisterCostAdjustmentDetailComponent implements OnChanges {

  @Input() costAdjustmentDetailContract: CostAdjustmentDetailModel = new CostAdjustmentDetailModel();
  @Input() costAdjustmentModel: CostAdjustmentModel = new CostAdjustmentModel();
  @Input() costAdjustmentDetails: CostAdjustmentDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  formCostAdjustmentDetail: FormGroup = new FormGroup({});
  costAdjustmentDetailId: number = 0;
  codeNameArticle: string = '';

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private costAdjustmentDetailService: CostAdjustmentDetailService,
    private formControlStateService: FormControlStateService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new CostAdjustmentDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const costAdjustmentDetails = changes["costAdjustmentDetails"];
    const costAdjustmentDetailContract = changes["costAdjustmentDetailContract"];
    if (costAdjustmentDetails && !costAdjustmentDetails.firstChange) {
      return await this.createCostAdjustmentDetails(this.costAdjustmentDetails);
    }
    if (costAdjustmentDetailContract && !costAdjustmentDetailContract.firstChange) {
      if (this.costAdjustmentDetailContract.costAdjustmentDetailId == 0) {
        this.createNewDetail();
      } else {
        this.costAdjustmentDetailId = this.costAdjustmentDetailContract.costAdjustmentDetailId;
        this.validateForm(this.costAdjustmentDetailContract);
      }
    }
    setTimeout(() => {
      this.formControlStateService.setControlState(this.formCostAdjustmentDetail, ['quantity', 'totalValue'], false);
    });
  }

  calculateValues(adjustedUnitCost: number) {
    const form = this.formCostAdjustmentDetail;
    const quantity = form.get("quantity")?.value ?? 0;
    const totalValue = quantity * adjustedUnitCost;
    form.patchValue({ totalValue });
  }

  clearForm() {
    this.codeNameArticle = '';
    this.costAdjustmentDetailId = 0;
    this.isFormView.emit(false);
    this.validateForm(new CostAdjustmentDetailModel());
  }

  createNewDetail() {
    this.codeNameArticle = '';
    this.costAdjustmentDetailId = 0;
    this.validateForm(this.costAdjustmentDetailContract);
  }

  async createCostAdjustmentDetails(costAdjustmentDetails: CostAdjustmentDetailModel[]) {
    try {
      await firstValueFrom(this.costAdjustmentDetailService.createSomeDetails(costAdjustmentDetails));
      this.toastHandlerService.showToast('success', 'Los detalles del ajuste fueron creados exitosamente');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del ajuste.', error);
    }
  }

  async createCostAdjustmentDetail(modelCostAdjustmentDetail: CostAdjustmentDetailModel) {
    try {
      await firstValueFrom(this.costAdjustmentDetailService.createCostAdjustmentDetail(modelCostAdjustmentDetail));
      this.toastHandlerService.showToast('success', `El detalle del ajuste fue creado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle del traslado.', error);
    }
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formCostAdjustmentDetail.valid) {
      return this.showValidationMessagesDetail();
    }
    const modelCostAdjustmentDetail = this.formCostAdjustmentDetail.getRawValue() as CostAdjustmentDetailModel;
    modelCostAdjustmentDetail.costAdjustmentId = this.costAdjustmentModel.costAdjustmentId;
    if (this.costAdjustmentDetailId == 0) {
      await this.createCostAdjustmentDetail(modelCostAdjustmentDetail);
    } else {
      await this.updateCostAdjustmentDetail(modelCostAdjustmentDetail);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      costAdjustmentId: {
        required: 'Debe seleccionar un Ajuste de Costo.',
        min: 'Debe seleccionar un Ajuste de Costo válido.'
      },
      articleId: {
        required: 'Debe seleccionar un Artículo.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      adjustedUnitCost: {
        required: 'Debe ingresar un ajuste de costo unitario válido.'
      },
      quantity: {
        required: 'Debe ingresar una Cantidad.',
        min: 'La Cantidad debe ser mayor a 0.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCostAdjustmentDetail.get(controlName);
      if (control?.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateCostAdjustmentDetail(modelCostAdjustmentDetail: CostAdjustmentDetailModel) {
    try {
      this.costAdjustmentDetailContract.adjustedUnitCost = modelCostAdjustmentDetail.adjustedUnitCost;
      await firstValueFrom(this.costAdjustmentDetailService.updateCostAdjustmentDetail(this.costAdjustmentDetailContract));
      this.toastHandlerService.showToast('success', `El detalle del ajuste fue modificado exitosamente.`);
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle del ajuste.', error);
    }
  }

  async validateForm(costAdjustmentDetail: CostAdjustmentDetailModel) {
    this.formCostAdjustmentDetail = this.formBuilder.group({
      costAdjustmentId: [costAdjustmentDetail.costAdjustmentId, [Validators.required, Validators.min(1)]],
      articleId: [costAdjustmentDetail.articleId, [Validators.required, Validators.min(1)]],
      adjustedUnitCost: [costAdjustmentDetail.adjustedUnitCost, [Validators.required]],
      quantity: [costAdjustmentDetail.quantity, [Validators.required, Validators.min(1)]],
      totalValue: [costAdjustmentDetail.totalValue],
    });
    const { articleId, adjustedUnitCost } = costAdjustmentDetail;
    if (articleId > 0) {
      this.isFormView.emit(true);
      await this.getArticle(articleId);
      this.calculateValues(adjustedUnitCost);
    }
  }

  validateFormControls() {
    Object.keys(this.formCostAdjustmentDetail.controls).forEach((key) => {
      const control = this.formCostAdjustmentDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
